import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useGetReplayQuery } from '@/services/qchinesepokerApi';
import { postoMio } from '@/lib/qcp/rest';
import { useHaptics } from '@/hooks/useHaptics';
import { useQcpMe } from '@/hooks/useQcpMe';
import { QcpRestNotice } from '@/components/chinesepoker/QcpRestNotice';
import { Loader } from '@/components/common/Loader';
import { OfcBoard } from '@/components/chinesepoker/OfcBoard';
import { PlayingCard } from '@/components/chinesepoker/PlayingCard';
import { cn } from '@/lib/cn';

/**
 * Read-only replay: one hand at a time, FINAL boards per seat (the server
 * keeps no per-round action log — see docs/HANDOFF-qcp-replay-actionlog.md).
 * Forfeited hands carry null boards and render a placeholder.
 */
export function ChinesePokerReplay() {
  const { id = '' } = useParams();
  const { t } = useTranslation('chinesepoker');
  const haptics = useHaptics();
  const mioUserId = useQcpMe();
  const { data: replay, isLoading, isError, error, refetch } = useGetReplayQuery(id, { skip: !id });
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }
  if (isError || replay === null) {
    // `null` = il BFF ha risposto con una forma che non e' un replay (contratto violato): errore, con «Riprova».
    return (
      <div className="m-4">
        <QcpRestNotice error={error} fallback={t('replay.error')} onRetry={() => void refetch()} />
      </div>
    );
  }
  if (!replay || replay.hands.length === 0) {
    return (
      <p className="m-4 rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
        {t('replay.error')}
      </p>
    );
  }

  const hands = replay.hands;
  const hand = hands[Math.min(idx, hands.length - 1)];
  // Senza `me` (o con un id che non e' in questa partita) la prospettiva e' quella del posto A, come prima.
  const mySeat = postoMio(replay.players, mioUserId) ?? 'A';
  const oppSeat = mySeat === 'A' ? 'B' : 'A';
  const myNet = mySeat === 'A' ? hand.netA : hand.netB;

  const step = (d: 1 | -1) => {
    haptics.selection();
    setIdx((i) => Math.min(Math.max(i + d, 0), hands.length - 1));
  };

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. L'icona era del titolo e se ne va con lui. */}
      <header className="sr-only">
        <h1>{t('replay.title')}</h1>
      </header>

      {/* Hand pager */}
      <div className="flex items-center justify-between rounded-xl bg-bg-secondary p-2">
        <button
          type="button"
          onClick={() => step(-1)}
          disabled={idx === 0}
          aria-label={t('replay.prev')}
          className="grid h-9 w-9 place-items-center rounded-lg text-fg transition-opacity active:opacity-70 disabled:opacity-30"
        >
          <ChevronLeft aria-hidden className="h-5 w-5" />
        </button>
        <div className="text-center">
          <p className="text-sm font-semibold text-fg">
            {t('hand.counter', { n: hand.handNumber })} / {hands.length}
          </p>
          <p
            className={cn(
              'text-xs font-semibold tabular-nums',
              myNet > 0 ? 'text-success' : myNet < 0 ? 'text-danger' : 'text-fg-muted',
            )}
          >
            {myNet > 0 ? `+${myNet}` : myNet}
          </p>
        </div>
        <button
          type="button"
          onClick={() => step(1)}
          disabled={idx === hands.length - 1}
          aria-label={t('replay.next')}
          className="grid h-9 w-9 place-items-center rounded-lg text-fg transition-opacity active:opacity-70 disabled:opacity-30"
        >
          <ChevronRight aria-hidden className="h-5 w-5" />
        </button>
      </div>

      {hand.boards === null ? (
        <p className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {t('replay.forfeit')}
        </p>
      ) : (
        <>
          {/* Opponent board (compact) */}
          <section className="space-y-1">
            <h2 className="text-sm font-medium text-fg-muted">{replay.players[oppSeat].displayName}</h2>
            <OfcBoard board={hand.boards[oppSeat]} compact />
            {hand.discards && hand.discards[oppSeat].length > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-fg-muted">{t('replay.discards')}:</span>
                {hand.discards[oppSeat].map((c) => (
                  <PlayingCard key={`${c.r}${c.s}`} card={c} />
                ))}
              </div>
            )}
          </section>

          {/* My board (full size) */}
          <section className="space-y-1">
            <h2 className="text-sm font-medium text-fg">{replay.players[mySeat].displayName}</h2>
            <OfcBoard board={hand.boards[mySeat]} />
            {hand.discards && hand.discards[mySeat].length > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-fg-muted">{t('replay.discards')}:</span>
                {hand.discards[mySeat].map((c) => (
                  <PlayingCard key={`${c.r}${c.s}`} card={c} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
